import {Trans} from '@lingui/macro';
import {Placement} from '@popperjs/core';
import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import noTeammates from '../../../../assets/images/noTeammates.svg';
import {CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {isEnhancedGlobalCard, isEnhancedPrivateCard} from '../../../../helpers/models/cardHelpers';
import {createVisualContactModel} from '../../../../helpers/models/contactHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {CardModel} from '../../../../models/api/cardModel';
import {ContactModel, parseContactUid} from '../../../../models/api/contactModel';
import {SearchCardsModel} from '../../../../models/api/searchCardModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {VisualContactModel} from '../../../../models/api/visualContactModel';
import {ApiSendOptions} from '../../../../services/http/http';
import {ResourceMap} from '../../../../store/appTypes';
import {Skeleton, SkeletonColorSchemesEnum} from '../../skeleton';
import {DropdownEmpty} from '../dropdownEmpty';
import {DropdownItemContactAvatar} from '../dropdownItemContactAvatar';
import {DropdownItemMultiline} from '../dropdownItemMultiline';
import {AsyncDropdownSource, DropdownFetcher} from '../source/asyncDropdownSource';
import {RowRendererOf} from '../source/dropdownSource';
import {DeprecatedSearchDropdown} from './deprecatedSearchDropdown';

/*
 * Constants.
 */

const dropdownHeight = 342;
const dropdownRowHeight = 48;
// 5 rows + 24px of padding
const maxDropdownContentHeight = dropdownRowHeight * 5 + 24;

/*
 * Props.
 */

export interface SearchContactsTooltipOptions {
  isDisabled: boolean;
  contactHandlesJoined: string;
  account?: AccountModel;
}

interface SearchContactsDropdownProps {
  popoverProps: PopoverTransferProps;
  teammates: ReadonlyArray<TeammateModel>;
  accounts: ResourceMap<AccountModel>;
  onlineTeammateIds: ReadonlySet<number> | undefined;
  searchPlaceholder: string;
  placement?: Placement;

  renderTooltip?: RendererOf<SearchContactsTooltipOptions>;
  computeIsContactDisabled?: (card: CardModel) => boolean;
  filterSearchResults: (card: CardModel) => boolean;
  searchForCardsAsync: (query: string, options: ApiSendOptions) => Promise<SearchCardsModel | undefined>;
  onConfirm: HandlerOf<CardModel>;
}

// We will always have a card with our implementation
type VisualContactModelWithCard = VisualContactModel & {card: CardModel};

/*
 * Style.
 */

const StyledDropdownEmpty = styled(DropdownEmpty)`
  padding: 82px 0;
`;

const StyledDropdownItem = styled(DropdownItem)`
  display: flex;
  flex-flow: row;
  height: 48px;
`;

const StyledDropdownContactContainerDiv = styled.div`
  margin-left: 12px;
  align-self: center;
`;

const StyledContactInformationContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
`;

const StyledDropdownSpacerDiv = styled.div`
  margin-left: 12px;
`;

const StyledContactHeaderDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const StyledContactNameDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  line-height: 20px;
  ${ellipsis()};
`;

const StyledContactNamespaceDiv = styled.div`
  flex: 0 0 12px;
  margin-left: 4px;
  margin-top: 4px;

  &:empty {
    flex: 0 1;
    margin: 0;
  }
`;

const StyledSearchContactLoadingContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  height: 48px;
`;

const StyledContactLoadingDiv = styled(StyledDropdownContactContainerDiv)`
  padding-top: 4px;
  padding-left: 12px;
  margin: 0;
  flex: 0 0 44px;
`;

const StyledLineSkeleton = styled(Skeleton)`
  margin-top: 4px;
`;

const StyledContactLoadingDetailsDiv = styled.div`
  flex: 1;
  padding-right: 12px;
`;

const StyledLoadingRendererContainerDiv = styled.div`
  padding: 7px 0;
`;

/*
 * Component.
 */

export const SearchContactsDropdown: FC<SearchContactsDropdownProps> = (props) => {
  const {
    popoverProps,
    teammates,
    accounts,
    onlineTeammateIds,
    searchPlaceholder,
    placement,
    computeIsContactDisabled,
    filterSearchResults,
    searchForCardsAsync,
    onConfirm,
    renderTooltip,
  } = props;

  /** Dropdown Setup */
  const fetcher: DropdownFetcher<VisualContactModelWithCard> = async (
    query: string,
    items: ReadonlyArray<VisualContactModelWithCard>,
    cancelToken: CancelToken,
  ) => {
    const response = await searchForCardsAsync(query, {cancelToken});
    cancelToken.throwIfRequested();

    // The request failed or was cancelled, return early
    if (!response) {
      return {
        items,
        canLoadMore: false,
      };
    }

    const parsedResults = _(response.cards)
      .filter((card) => card.contactUids.length !== 0)
      .filter(filterSearchResults)
      .map((card) => {
        const selectedContact = response.contacts.find((contact) => contact.uid === card.contactUids[0]);
        if (!selectedContact) {
          return undefined;
        }

        return createVisualContact(card, selectedContact, teammates, onlineTeammateIds);
      })
      .compact()
      .value();

    return {
      items: parsedResults,
      canLoadMore: false,
    };
  };

  const renderEmpty = (query: string) => {
    if (query) {
      return <StyledDropdownEmpty />;
    }
    return (
      <StyledDropdownEmpty imageSource={noTeammates}>
        <Trans id="search-contacts-dropdown-empty-placeholder">Type to search</Trans>
      </StyledDropdownEmpty>
    );
  };

  const renderRow: RowRendererOf<VisualContactModelWithCard> = (
    contact: VisualContactModelWithCard,
    _index: number,
  ) => {
    const isDisabled = Boolean(computeIsContactDisabled && computeIsContactDisabled(contact.card));
    const account = contact.card.accountId ? accounts[contact.card.accountId] : undefined;
    const contactHandlesJoined = contact.card.contactUids
      .map((contactUid) => parseContactUid(contactUid)[1])
      .join(', ');
    return (
      <StyledDropdownItem
        key={contact.card.id}
        onSelect={() => {
          onConfirm(contact.card);
          popoverProps.onRequestClose();
        }}
        isDisabled={isDisabled}
      >
        <StyledDropdownContactContainerDiv>
          <DropdownItemContactAvatar contact={contact} />
        </StyledDropdownContactContainerDiv>
        <StyledContactInformationContainerDiv>
          <TooltipCoordinator
            render={() =>
              renderTooltip
                ? renderTooltip({isDisabled, contactHandlesJoined, account})
                : contactHandlesJoined
            }
            placement="top"
          >
            <DropdownItemMultiline
              title={renderDropdownItemTitle(contact)}
              subtitle={
                <>
                  {contactHandlesJoined}
                  {maybeRenderAccount(account)}
                </>
              }
            />
          </TooltipCoordinator>
        </StyledContactInformationContainerDiv>
        <StyledDropdownSpacerDiv />
      </StyledDropdownItem>
    );
  };

  const asyncDropdownSource = new AsyncDropdownSource<VisualContactModelWithCard>({
    fetcher,
    renderRow,
    renderEmpty,
    initialItems: [],
    rowHeightPredicate: () => dropdownRowHeight,
  });

  return (
    <DeprecatedSearchDropdown
      {...popoverProps}
      title={<Trans id="search-contacts-dropdown-title">Contacts</Trans>}
      placeholder={searchPlaceholder}
      source={asyncDropdownSource}
      placement={placement || 'top-end'}
      maxContentHeight={maxDropdownContentHeight}
      height={dropdownHeight}
      shouldIncludeSearchIcon
      renderLoading={() => (
        <StyledLoadingRendererContainerDiv>
          {renderContactLoadingRow()}
          {renderContactLoadingRow()}
          {renderContactLoadingRow()}
          {renderContactLoadingRow()}
          {renderContactLoadingRow()}
        </StyledLoadingRendererContainerDiv>
      )}
    />
  );
};

/*
 * Render helpers.
 */

const maybeRenderAccount = (account?: AccountModel) => {
  if (!account) {
    return null;
  }

  return <> • {account.name}</>;
};

const renderContactLoadingRow = () => (
  <StyledSearchContactLoadingContainerDiv>
    <StyledContactLoadingDiv>
      <Skeleton width="20px" height="20px" borderRadius="50%" />
    </StyledContactLoadingDiv>
    <StyledContactLoadingDetailsDiv>
      <StyledLineSkeleton height="16px" width="120px" />
      <StyledLineSkeleton height="16px" scheme={SkeletonColorSchemesEnum.GREY10} />
    </StyledContactLoadingDetailsDiv>
  </StyledSearchContactLoadingContainerDiv>
);

const renderDropdownItemTitle = (contact: VisualContactModelWithCard) => (
  <StyledContactHeaderDiv>
    <StyledContactNameDiv>{contact.name ?? contact.card.name}</StyledContactNameDiv>
    <StyledContactNamespaceDiv>{renderCardNamespace(contact.card)}</StyledContactNamespaceDiv>
  </StyledContactHeaderDiv>
);

const renderCardNamespace = (card: CardModel) => {
  if (isEnhancedPrivateCard(card)) {
    return (
      <UserThemeConsumer>
        {(theme) => <Icon name="lockFilledNewTiny" size={12} color={theme.greys.shade70} />}
      </UserThemeConsumer>
    );
  }

  if (isEnhancedGlobalCard(card)) {
    return (
      <UserThemeConsumer>
        {(theme) => <Icon name="buildingFilledTiny" size={12} color={theme.palette.teal.shade40} />}
      </UserThemeConsumer>
    );
  }

  // Do not display anything as it is an auto created card
  return null;
};

/*
 * Helpers.
 */

const createVisualContact = (
  card: CardModel,
  contact: ContactModel,
  teammates: ReadonlyArray<TeammateModel>,
  onlineTeammateIds: ReadonlySet<number> | undefined,
): VisualContactModelWithCard => {
  const teammate = teammates && teammates.find((tm) => tm.email === contact.handle);
  return createVisualContactModel({
    contact,
    namespace: card.namespace,
    selectedCard: card,
    name: card.name,
    teammate,
    onlineTeammateIds,
    fallbackAvatarUrl: card.avatarUrl,
  }) as VisualContactModelWithCard;
};
